import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SectionHeadingProps {
    eyebrow: string
    title: ReactNode
    description?: ReactNode
    align?: 'left' | 'center'
    tone?: 'default' | 'inverted'
    as?: 'h1' | 'h2'
    id?: string
    className?: string
}

export const SectionHeading: FC<SectionHeadingProps> = ({
    eyebrow,
    title,
    description,
    align = 'left',
    tone = 'default',
    as: Heading = 'h2',
    id,
    className
}): ReactNode => {
    const inverted = tone === 'inverted'

    return (
        <div className={cn('max-w-2xl', align === 'center' && 'mx-auto text-center', className)}>
            <p className={cn('inline-flex items-center gap-2 font-mono text-xs font-medium tracking-wider uppercase', inverted ? 'text-accent' : 'text-primary')}>
                <span aria-hidden='true'>{'//'}</span>
                {eyebrow}
            </p>

            <Heading
                id={id}
                className={cn(
                    'mt-3 font-semibold',
                    Heading === 'h1' ? 'text-4xl sm:text-5xl lg:text-6xl lg:leading-[1.05]' : 'text-3xl sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]',
                    inverted ? 'text-background' : 'text-foreground'
                )}
            >
                {title}
            </Heading>

            {description && (
                <p className={cn('mt-4 text-base leading-relaxed sm:text-lg', inverted ? 'text-background/70' : 'text-muted-foreground')}>
                    {description}
                </p>
            )}
        </div>
    )
}
